import { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";

import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Users, UserPlus, MoreVertical, Mail, Crown, AlertCircle } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useOrganizationManagement, type OrgMember } from "@/hooks/useOrganizationManagement";
import { useOrganizationFeatures } from "@/hooks/useOrganizationFeatures";
import { toast } from "@/hooks/use-toast";

export default function OrganizationManagement() {
  console.warn('🚀 COMPONENT LOADED - OrganizationManagement v3');
  
  const { user } = useAuth();
  console.warn('👤 User from useAuth:', user?.email);
  
  const { organizationData, canManageMembers, canAddMoreSeats, refreshOrganizationData } = useOrganizationFeatures();
  
  console.warn('📊 Data from useOrganizationFeatures:', {
    hasOrgData: !!organizationData,
    orgName: organizationData?.name,
    totalSeats: organizationData?.features?.totalSeats,
    usedSeats: organizationData?.usedSeats,
    brandingEnabled: organizationData?.features?.brandingProEnabled
  });
  const { inviteMember, fetchMembers, updateMemberRole, removeMember } = useOrganizationManagement();
  
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "member">("member");
  const [inviting, setInviting] = useState(false);

  const loadMembers = useCallback(async () => {
    if (!organizationData?.id) return;
    
    setLoading(true);
    const { data } = await fetchMembers(organizationData.id);
    setMembers(data);
    setLoading(false);
  }, [organizationData?.id, fetchMembers]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const handleInvite = async () => {
    if (!organizationData?.id || !inviteEmail.trim()) return;

    if (!canAddMoreSeats) {
      toast({
        title: "Quota atteint",
        description: `Vous avez atteint la limite de ${organizationData.features.totalSeats} siège(s). Mettez à niveau votre plan.`,
        variant: "destructive",
      });
      return;
    }

    setInviting(true);
    const { error } = await inviteMember(organizationData.id, inviteEmail.trim(), inviteRole);
    
    if (error) {
      toast({
        title: "Erreur d'invitation",
        description: error.userMessage || error.message || "Impossible d'envoyer l'invitation",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Invitation envoyée",
        description: `Une invitation a été envoyée à ${inviteEmail}`,
      });
      setInviteDialogOpen(false);
      setInviteEmail("");
      setInviteRole("member");
      loadMembers();
    }
    setInviting(false);
  };

  const handleRoleUpdate = async (userId: string, newRole: "admin" | "member") => {
    if (!organizationData?.id) return;
    
    const { error } = await updateMemberRole(organizationData.id, userId, newRole);
    if (error) {
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour le rôle",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Rôle mis à jour",
        description: "Le rôle du membre a été mis à jour",
      });
      loadMembers();
    }
  };

  const handleRemoveMember = async (userId: string, memberName: string) => {
    if (!organizationData?.id) return;
    
    const { error } = await removeMember(organizationData.id, userId);
    if (error) {
      toast({
        title: "Erreur",
        description: "Impossible de retirer le membre",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Membre retiré",
        description: `${memberName} a été retiré de l'organisation`,
      });
      loadMembers();
    }
  };

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (!canManageMembers) {
    return <Navigate to="/dashboard" replace />;
  }

  const activeMembers = members.filter(m => m.status === "active");
  const pendingInvites = members.filter(m => m.status === "invited");

  return (
    <AppLayout>
      <div className="flex-1 flex flex-col px-0 transition-all duration-300 font-sans">
        <main className="flex-1 p-6">
          <div className="w-full flex flex-col gap-8">
            <div className="pt-4">
              <h1 className="text-3xl font-semibold text-gray-900 leading-tight font-urbanist mb-2">
                Gestion d'équipe
              </h1>
              <p className="text-muted-foreground">
                Gérez les membres de {organizationData?.name || "votre organisation"}
              </p>
            </div>

              {/* Quota Display */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Utilisation des sièges
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-2xl font-bold">
                        {organizationData?.usedSeats} / {organizationData?.features.totalSeats}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Sièges utilisés
                      </p>
                      {/* Debug temporaire */}
                      <button 
                        onClick={() => {
                          console.warn('🔍 Debug organizationData:', organizationData);
                          console.warn('🔍 Features:', organizationData?.features);
                          console.warn('🔍 Total seats:', organizationData?.features.totalSeats);
                          console.warn('🔍 Used seats:', organizationData?.usedSeats);
                          console.warn('🔍 Can add more seats:', canAddMoreSeats);
                          refreshOrganizationData();
                        }}
                        className="text-xs text-blue-500 underline mt-1"
                      >
                        Debug & Refresh
                      </button>
                    </div>
                    <Badge variant={canAddMoreSeats ? "default" : "destructive"}>
                      {organizationData?.features.brandingProEnabled ? "Branding Pro" : "Standard"}
                    </Badge>
                  </div>
                  {!canAddMoreSeats && (
                    <div className="mt-4 p-3 bg-destructive/10 rounded-lg flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-destructive" />
                      <p className="text-sm text-destructive">
                        Quota de sièges atteint. Mettez à niveau pour ajouter plus de membres.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Tabs defaultValue="members" className="w-full">
                <TabsList className="grid w-full grid-cols-2 h-12 mb-8">
                  <TabsTrigger value="members" className="text-base">
                    Membres ({activeMembers.length})
                  </TabsTrigger>
                  <TabsTrigger value="invitations" className="text-base">
                    Invitations ({pendingInvites.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="members" className="mt-0">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <div>
                        <CardTitle>Membres actifs</CardTitle>
                        <CardDescription>
                          Gérez les rôles et permissions des membres
                        </CardDescription>
                      </div>
                      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
                        <DialogTrigger asChild>
                          <Button disabled={!canAddMoreSeats}>
                            <UserPlus className="w-4 h-4 mr-2" />
                            Inviter un membre
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Inviter un nouveau membre</DialogTitle>
                            <DialogDescription>
                              Envoyez une invitation pour rejoindre l'organisation
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <Label htmlFor="email">Email professionnel</Label>
                              <Input
                                id="email"
                                type="email"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                placeholder="nom@entreprise.com"
                              />
                            </div>
                            <div>
                              <Label htmlFor="role">Rôle</Label>
                              <Select value={inviteRole} onValueChange={(value: "admin" | "member") => setInviteRole(value)}>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="member">Membre</SelectItem>
                                  <SelectItem value="admin">Administrateur</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <Button 
                              onClick={handleInvite} 
                              disabled={!inviteEmail.trim() || inviting}
                              className="w-full"
                            >
                              {inviting ? "Envoi..." : "Envoyer l'invitation"}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Nom</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Rôle</TableHead>
                            <TableHead>Statut</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {loading ? (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center">
                                Chargement...
                              </TableCell>
                            </TableRow>
                          ) : activeMembers.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center">
                                Aucun membre actif
                              </TableCell>
                            </TableRow>
                          ) : (
                            activeMembers.map((member) => (
                              <TableRow key={member.id}>
                                <TableCell>
                                  {member.profile?.first_name} {member.profile?.last_name}
                                </TableCell>
                                <TableCell>{member.profile?.email}</TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    {member.role === "owner" && <Crown className="w-4 h-4 text-yellow-500" />}
                                    <Badge variant={member.role === "owner" ? "default" : "secondary"}>
                                      {member.role === "owner" ? "Propriétaire" : 
                                       member.role === "admin" ? "Admin" : "Membre"}
                                    </Badge>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge variant="default">Actif</Badge>
                                </TableCell>
                                <TableCell>
                                  {member.role !== "owner" && member.user_id !== user.id && (
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="sm">
                                          <MoreVertical className="w-4 h-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuItem 
                                          onClick={() => {
                                            if (member.user_id) {
                                              handleRoleUpdate(member.user_id, member.role === "admin" ? "member" : "admin");
                                            }
                                          }}
                                        >
                                          {member.role === "admin" ? "Rétrograder en membre" : "Promouvoir en admin"}
                                        </DropdownMenuItem>
                                        <DropdownMenuItem 
                                          className="text-destructive"
                                          onClick={() => {
                                            if (member.user_id) {
                                              handleRemoveMember(member.user_id, `${member.profile?.first_name || ''} ${member.profile?.last_name || ''}`);
                                            }
                                          }}
                                        >
                                          Retirer de l'équipe
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="invitations" className="mt-0">
                  <Card>
                    <CardHeader>
                      <CardTitle>Invitations en attente</CardTitle>
                      <CardDescription>
                        Invitations envoyées qui n'ont pas encore été acceptées
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Email</TableHead>
                            <TableHead>Rôle</TableHead>
                            <TableHead>Invité le</TableHead>
                            <TableHead>Statut</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pendingInvites.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} className="text-center">
                                <div className="flex flex-col items-center gap-2 py-6">
                                  <Mail className="w-8 h-8 text-muted-foreground" />
                                  <p className="text-muted-foreground">Aucune invitation en attente</p>
                                </div>
                              </TableCell>
                            </TableRow>
                          ) : (
                            pendingInvites.map((invite) => (
                              <TableRow key={invite.id}>
                                <TableCell>{invite.profile?.email}</TableCell>
                                <TableCell>
                                  <Badge variant="secondary">
                                    {invite.role === "admin" ? "Admin" : "Membre"}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  {invite.invited_at ? new Date(invite.invited_at).toLocaleDateString() : "-"}
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline">En attente</Badge>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
          </div>
        </main>
      </div>
    </AppLayout>
  );
}